
import ChevronIcon from "@/components/icons/ChevronIcon.vue";
import { Vue, Component, Prop } from "vue-property-decorator";
import { Project, User } from "@/generated/graphql";
import GET_PROJECT_CLIENT from "@/apollo/projectActivities/GetProjectClient.gql";
import BasicInformation from "../ClientRightSide/BasicInformation.vue";
import RequiredServices from "../ClientRightSide/RequiredServices.vue";
import EmergencyContact from "../ClientRightSide/EmergencyContact.vue";
import AccessInformation from "../ClientRightSide/AccessInformation.vue";
import LanguageCommunication from "../ClientRightSide/LanguageCommunication.vue";
import HealthInformation from "../ClientRightSide/HealthInformation.vue";
import FunctionalAssessment from "../ClientRightSide/FunctionalAssessment.vue";
import PersonalConsiderations from "../ClientRightSide/PersonalConsiderations.vue";
import SupportNetwork from "../ClientRightSide/SupportNetwork.vue";
import CareGoals from "../ClientRightSide/CareGoals.vue";
import AdditionalNotes from "../ClientRightSide/AdditionalNotes.vue";
import _ from "lodash";

interface ClientData {
  client?: any; // Replace with proper client interface
}

@Component({
  components: {
    ChevronIcon,
    BasicInformation,
    RequiredServices,
    EmergencyContact,
    AccessInformation,
    LanguageCommunication,
    HealthInformation,
    FunctionalAssessment,
    PersonalConsiderations,
    SupportNetwork,
    CareGoals,
    AdditionalNotes,
  },
  apollo: {
    projectData: {
      query: GET_PROJECT_CLIENT,
      variables() {
        return {
          where: {
            id: {
              _eq: this.$route.params.id,
            },
          },
        };
      },
      update({ project }) {
        if (project?.[0]) {
          this.clientData = project[0];
          return project[0];
        }
        return null;
      },
      error(error) {
        console.error("Error fetching client data:", error);
      },
    },
  },
})
export default class ClientsNew extends Vue {
  @Prop({ type: Object, required: true }) readonly project!: Project;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;

  activeTab = "basic";
  showMobileSidebar = false;
  clientData: ClientData = {};
  projectData: any = null;
  currentEditTab = { tab: 0, step: 0 };
  tabMapping: Record<string, { tab: number; step: number }> = {
    basic: { tab: 0, step: 0 },
    access: { tab: 0, step: 0 },
    required: { tab: 1, step: 0 },
    emergency: { tab: 1, step: 1 },
    language: { tab: 1, step: 2 },
    health: { tab: 1, step: 3 },
    assessment: { tab: 1, step: 4 },
    considerations: { tab: 1, step: 5 },
    network: { tab: 1, step: 6 },
    goals: { tab: 1, step: 7 },
    notes: { tab: 1, step: 8 },
  };
  sidebarTabs = [
    { id: "basic", name: "Basic Information" },
    { id: "required", name: "Required Services" },
    { id: "emergency", name: "Emergency Contact" },
    { id: "access", name: "Access Information" },
    { id: "language", name: "Language & Communication" },
    { id: "health", name: "Health Information" },
    { id: "assessment", name: "Functional Assessment" },
    { id: "considerations", name: "Personal Considerations" },
    { id: "network", name: "Support Network" },
    { id: "goals", name: "Care Goals" },
    { id: "notes", name: "Additional Notes" },
  ];

  get currentTabFields() {
    const tabKey =
      this.activeTab === "basic" ? "basicInformation" : `${this.activeTab}`;
    const data = this.clientData[tabKey];
    if (!data) return [];

    // Flatten nested objects and arrays
    const flattenObject = (obj: any, parentKey = ""): any[] => {
      let result = [];

      for (const [key, value] of Object.entries(obj)) {
        const currentKey = parentKey
          ? `${parentKey} ${this.formatLabel(key)}`
          : key;

        if (Array.isArray(value)) {
          // Handle array of objects
          if (value.length > 0 && typeof value[0] === "object") {
            const formattedArray = value.map((item) =>
              Object.entries(item)
                .map(([k, v]) => `${this.formatLabel(k)}: ${v}`)
                .join(", ")
            );
            result.push({
              label: this.formatLabel(currentKey),
              value: formattedArray.join("\n"),
            });
          } else {
            // Handle simple arrays
            result.push({
              label: this.formatLabel(currentKey),
              value: value.join(", "),
            });
          }
        } else if (typeof value === "object" && value !== null) {
          // Recursively flatten nested objects
          result = result.concat(flattenObject(value, currentKey));
        } else {
          result.push({
            label: this.formatLabel(currentKey),
            value: value || "-",
          });
        }
      }

      return result;
    };

    return flattenObject(data);
  }

  formatLabel(key: string): string {
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  }

  get getActiveTabName(): string {
    return (
      this.sidebarTabs.find((tab) => tab.id === this.activeTab)?.name || ""
    );
  }

  convertArrayToOptions(
    array: string[] | null | undefined
  ): Array<{ label: string; value: string }> {
    if (!Array.isArray(array)) return []; // Ensure it's an array
    return array.map((item) => ({
      label: item,
      value: item,
    }));
  }

  convertOptionsToArray(
    options: Array<{ label: string; value: string }>
  ): string[] {
    if (!options) return [];
    return options.map((option) => option.value);
  }

  formatDataForModal(data: any): any {
    const formattedData = _.cloneDeep(data);

    // Define arrays and select fields
    const arrayFields = [
      "pets",
      "hobbies",
      "allergies_food",
      "allergies_medication",
      "allergies_environmental",
      "support_type",
      "tags",
    ];

    const selectFields = [
      "access_type",
      "self_directed_care_preferences",
      "emergency_relationship",
      "communication_ability",
      "communication_languages_spoken",
      "preferred_language",
      "mobility_status",
      "dnr_order",
      "visual_communication_style",
      "preferred_communication_style",
      "interpreter_needed",
      "frequency",
      "support_type",
    ];

    // Convert array fields to options format
    arrayFields.forEach((field) => {
      if (formattedData[field]) {
        formattedData[field] = this.convertArrayToOptions(formattedData[field]);
      }
    });

    // Convert single select fields to option format
    selectFields.forEach((field) => {
      if (formattedData[field] && !arrayFields.includes(field)) {
        formattedData[field] = {
          label: formattedData[field],
          value: formattedData[field],
        };
      }
    });

    return formattedData;
  }

  private formatDataForSubmission(data: any): any {
    const formattedData = _.cloneDeep(data);

    const arrayFields = [
      "pets",
      "hobbies",
      "allergies_food",
      "allergies_medication",
      "allergies_environmental",
      "support_type",
      "tags",
    ];

    const selectFields = [
      "access_type",
      "self_directed_care_preferences",
      "emergency_relationship",
      "communication_ability",
      "communication_languages_spoken",
      "preferred_language",
      "mobility_status",
      "dnr_order",
      "visual_communication_style",
      "preferred_communication_style",
      "interpreter_needed",
      "frequency",
      "support_type",
    ];

    // Convert array fields back to simple arrays
    arrayFields.forEach((field) => {
      if (formattedData[field]) {
        formattedData[field] = this.convertOptionsToArray(formattedData[field]);
      }
    });

    // Convert single select fields back to simple values
    selectFields.forEach((field) => {
      if (formattedData[field] && !arrayFields.includes(field)) {
        formattedData[field] = formattedData[field].value;
      }
    });

    return formattedData;
  }
}
